import React, { useState } from "react";
import { toast } from "sonner";
import { Coins, Package, ShoppingBag, Zap } from "lucide-react";
import useFetch from "@/lib/useFetch";
import api from "@/lib/api";
import { PageHeader, Footer, SectionCard } from "@/components/common/Page";
import Loader from "@/components/common/Loader";
import { themeFor } from "@/lib/communities";
import { Button } from "@/components/ui/button";

const CUR = { scrap: "Scrap", hc: "HC", credits: "Credits" };

export default function Market() {
    const { data: communities } = useFetch("/communities");
    const { data: wallets, reload: reloadWallets } = useFetch("/me/wallets");
    const { data: shop } = useFetch("/shop");
    const { data: kits } = useFetch("/kits");
    const [active, setActive] = useState("ALL");

    if (!communities || !wallets || !shop || !kits) return <Loader />;

    const buy = async (url, name) => {
        try {
            const { data } = await api.post(url);
            toast.success(data.message, { description: `New balance: ${data.balance.toLocaleString()} ${CUR[data.currency]}` });
            reloadWallets();
        } catch (e) {
            toast.error("Purchase failed", { description: e.response?.data?.detail || "Try again." });
        }
    };

    const filteredShop = active === "ALL" ? shop : shop.filter((i) => i.community_key === active);
    const filteredKits = active === "ALL" ? kits : kits.filter((k) => k.community_key === active);

    return (
        <div data-testid="player-market">
            <PageHeader title="Market" subtitle="Each community has its own economy. No cross-community transfers." />

            {/* wallets */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {wallets.map((w) => {
                    const t = themeFor({ theme: w.theme });
                    return (
                        <div key={w.community_key} data-testid={`wallet-${w.community_key}`}
                            className="tactical-card rounded-lg p-4" style={{ borderTop: `3px solid ${t.accent}` }}>
                            <div className="flex items-center gap-2">
                                <Coins size={16} style={{ color: t.accent }} />
                                <span className="font-heading font-bold uppercase text-foreground">{w.community_name}</span>
                            </div>
                            <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                                <Bal label="Scrap" value={w.scrap} />
                                <Bal label="HC" value={w.hc} />
                                <Bal label="Credits" value={w.credits} />
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* community filter */}
            <div className="flex flex-wrap gap-2 mb-5">
                {["ALL", ...communities.map((c) => c.key)].map((k) => (
                    <button key={k} data-testid={`market-filter-${k}`} onClick={() => setActive(k)}
                        className={`px-3 py-1.5 rounded-sm label-mono border transition-colors ${active === k ? "border-hazard text-hazard bg-hazard/10" : "border-border text-muted-foreground hover:text-foreground"}`}>
                        {k === "ALL" ? "ALL" : communities.find((c) => c.key === k)?.name}
                    </button>
                ))}
            </div>

            <SectionCard title="Shop" testId="market-shop" className="mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {filteredShop.map((i) => (
                        <div key={i.id} data-testid={`shop-item-${i.id}`} className="rounded-md border border-border bg-background/40 p-4 flex flex-col">
                            <div className="flex items-center justify-between">
                                <ShoppingBag size={16} className="text-hazard" />
                                <span className="label-mono text-muted-foreground">{i.category}</span>
                            </div>
                            <div className="font-heading text-lg font-bold uppercase text-foreground mt-2">{i.name}</div>
                            <p className="text-sm text-muted-foreground flex-1">{i.description}</p>
                            <div className="flex items-center justify-between mt-3">
                                <span className="font-heading text-lg font-bold text-radioactive">{i.price.toLocaleString()} <span className="text-xs text-muted-foreground">{CUR[i.currency]}</span></span>
                                <Button data-testid={`buy-${i.id}`} size="sm" className="font-heading uppercase bg-hazard text-black hover:bg-hazard/90"
                                    onClick={() => buy(`/shop/${i.id}/buy`, i.name)}>Buy</Button>
                            </div>
                        </div>
                    ))}
                </div>
            </SectionCard>

            <SectionCard title="Kits" testId="market-kits">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {filteredKits.map((k) => (
                        <div key={k.id} data-testid={`kit-${k.id}`} className="rounded-md border border-border bg-background/40 p-4 flex flex-col">
                            <div className="flex items-center justify-between">
                                <Package size={16} className="text-burnt" />
                                <span className="label-mono text-muted-foreground">CD {k.cooldown}</span>
                            </div>
                            <div className="font-heading text-lg font-bold uppercase text-foreground mt-2">{k.name}</div>
                            <p className="text-sm text-muted-foreground">{k.description}</p>
                            <div className="flex flex-wrap gap-1 mt-2 flex-1">
                                {(k.contents || []).map((c) => (
                                    <span key={c} className="label-mono text-muted-foreground border border-border rounded-sm px-1.5 py-0.5">{c}</span>
                                ))}
                            </div>
                            <div className="flex items-center justify-between mt-3">
                                <span className="font-heading text-lg font-bold text-radioactive">{k.price === 0 ? "FREE" : `${k.price.toLocaleString()} ${CUR[k.currency]}`}</span>
                                <Button data-testid={`redeem-${k.id}`} size="sm" className="font-heading uppercase bg-burnt text-white hover:bg-burnt/90"
                                    onClick={() => buy(`/kits/${k.id}/redeem`, k.name)}>Redeem</Button>
                            </div>
                        </div>
                    ))}
                </div>
            </SectionCard>
            <Footer />
        </div>
    );
}

function Bal({ label, value }) {
    return (
        <div className="rounded-md border border-border bg-background/50 p-2">
            <div className="font-heading text-lg font-bold text-foreground tabular-nums">{(value || 0).toLocaleString()}</div>
            <div className="label-mono text-muted-foreground">{label}</div>
        </div>
    );
}
